// The calendar widget in the right-hand column of partials/new-base.html
// (partials/new-calendar-widget.html): month grid, the day's conversations with
// their popups, document upload and the notetaker status. Moved unchanged from
// Velzon's app.js; it runs after every page script (it mounts
// window.ContextLabelPicker from js/pages/context-label-picker.js).
/* global moment */

function _cwEl(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}

function _cwIsConversationProcessing(status) {
    var normalized = String(status || "").trim().toLowerCase();
    return normalized === "checking" || normalized === "sending" || normalized === "generating" || normalized === "pending";
}

function _cwIsConversationGenerated(status) {
    return String(status || "").trim().toLowerCase() === "generated";
}

function _cwDodgeBlockReason(isConversationProcessing, isMeetingNotStarted, ev) {
    if (isConversationProcessing) return "Unavailable while conversation is generating.";
    if (!isMeetingNotStarted) return "Unavailable after meeting has started.";
    if (!ev.is_single_organisation) return "Unavailable for non-single organisation conversations.";
    return null;
}

function _cwReadDodgeResponse(res) {
    if (res.ok) return { ok: true };
    return res.json().catch(function () { return {}; }).then(function (body) {
        return { ok: false, status: res.status, error: body && body.error ? body.error : "" };
    });
}

/* The conversation popup sits at z-index 1080, so widget modals have to
   be lifted above it. Every backdrop is raised, not just the newest: when
   one modal opens as another closes, the outgoing backdrop can still be in
   the DOM and the live one is not reliably last. The next-frame repeat
   catches a backdrop created after the shown event. */
/* The widget partial is included inside .RightSidebarColumn, which is
   position:fixed with z-index 1001 and so opens a stacking context. A
   modal left inside it can never rise above the body-level backdrop,
   whatever z-index it carries, so it is reparented to the body first. */
function _cwDetachWidgetModal(el) {
    if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
    }
}

function _cwLiftWidgetModal(el, z) {
    if (!el) return;
    var apply = function () {
        document.querySelectorAll(".modal-backdrop").forEach(function (backdrop) {
            backdrop.style.zIndex = String(z - 1);
        });
        el.style.zIndex = String(z);
    };
    apply();
    if (window.requestAnimationFrame) window.requestAnimationFrame(apply);
}

function _cwGetCsrfToken() {
    var match = document.cookie.match(/(?:^|;\s*)csrftoken=([^\s;]+)/);
    return match ? decodeURIComponent(match[1]) : "";
}

function calendarWidgetFmtYmd(d) {
    return d.getFullYear() + "-" +
        String(d.getMonth() + 1).padStart(2, "0") + "-" +
        String(d.getDate()).padStart(2, "0");
}

// A chosen date only holds for the day it was picked on; the next day the
// widget opens on today again.
function getCalendarWidgetChosenDateYmd() {
    var todayYmd = calendarWidgetFmtYmd(new Date());
    try {
        var raw = localStorage.getItem("calendarWidgetChosenDate");
        var chosenOn = localStorage.getItem("calendarWidgetChosenOn");
    } catch (e) { /* storage or focus unavailable: keep going */ }
    if (chosenOn !== todayYmd) return todayYmd;
    return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : todayYmd;
}

function setCalendarWidgetChosenDate(ymd) {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return;
    try {
        localStorage.setItem("calendarWidgetChosenOn", calendarWidgetFmtYmd(new Date()));
        localStorage.setItem("calendarWidgetChosenDate", ymd);
    } catch (e) { /* storage or focus unavailable: keep going */ }
    loadCalendarWidgetConversations();
    var calEl = document.getElementById("calendarWidgetCalendar");
    if (calEl && typeof calEl._cwMonthGridRender === "function") {
        try { calEl._cwMonthGridRender(); } catch (err) { /* a failed redraw keeps the previous grid */ }
    }
}

function initCalendarWidgetSidebar() {
    var toggleBtn = document.getElementById("calendarWidgetToggle");
    var sidebar = document.getElementById("calendarWidget");
    if (!toggleBtn || !sidebar) return;

    var storageKey = "calendarWidgetOpen";
    var stored = localStorage.getItem(storageKey);
    var isOpen = stored === null ? true : stored === "1";

    function applyState(open) {
        document.body.classList.toggle("calendar-open", open);
        document.body.classList.toggle("calendar-closed", !open);
        document.documentElement.classList.toggle("calendar-open", open);
        document.documentElement.classList.toggle("calendar-closed", !open);
        toggleBtn.classList.toggle("is-active", open);
        toggleBtn.setAttribute("aria-pressed", String(open));
        localStorage.setItem(storageKey, open ? "1" : "0");
        isOpen = open;
    }

    applyState(isOpen);
    toggleBtn.addEventListener("click", function () { applyState(!isOpen); });
}

function initCalendarWidgetMonthGrid() {
    var todayBtn = document.getElementById("calendarWidgetTodayBtn");
    if (todayBtn && !todayBtn._cwTodayBound) {
        todayBtn._cwTodayBound = true;
        todayBtn.addEventListener("click", function () {
            setCalendarWidgetChosenDate(calendarWidgetFmtYmd(new Date()));
        });
    }
    var el = document.getElementById("calendarWidgetCalendar");
    if (!el || typeof moment === "undefined") return;

    var weekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];

    function getChosenMoment() {
        var m = moment(getCalendarWidgetChosenDateYmd(), "YYYY-MM-DD", true);
        return m.isValid() ? m : moment();
    }

    function navigateMonth(delta) {
        var chosen = getChosenMoment();
        var target = chosen.clone().startOf("month").add(delta, "month");
        var day = Math.min(Math.max(chosen.date(), 1), target.daysInMonth());
        setCalendarWidgetChosenDate(target.clone().date(day).format("YYYY-MM-DD"));
    }

    var _cwCountsCache = {};

    function fetchEventCounts(monthKey) {
        if (_cwCountsCache[monthKey]) {
            applyEventBadges(_cwCountsCache[monthKey]);
            return;
        }
        fetch("/calendar/widget/conversations/counts/?date=" + encodeURIComponent(monthKey + "-01"), { credentials: "same-origin" })
            .then(function (res) { return res.ok ? res.json() : {}; })
            .then(function (counts) {
                _cwCountsCache[monthKey] = counts;
                applyEventBadges(counts);
            })
            .catch(function () {});
    }

    function applyEventBadges(counts) {
        var buttons = el.querySelectorAll(".CalendarWidgetMonthGrid__day[data-ymd]");
        for (var i = 0; i < buttons.length; i++) {
            var btn = buttons[i];
            var ymd = btn.getAttribute("data-ymd");
            var existing = btn.querySelector(".CalendarWidgetMonthGrid__badge");
            if (existing) existing.remove();
            if (counts[ymd] && counts[ymd] > 0) {
                var badge = _cwEl("span", "CalendarWidgetMonthGrid__badge", String(counts[ymd]));
                btn.appendChild(badge);
            }
        }
    }

    function render() {
        var chosen = getChosenMoment();
        var displayMonth = chosen.clone().startOf("month");
        var todayYmd = moment().format("YYYY-MM-DD");
        var chosenYmd = chosen.format("YYYY-MM-DD");

        el.innerHTML = "";

        var wrapper = _cwEl("div", "CalendarWidgetMonthGrid");
        var header = _cwEl("div", "CalendarWidgetMonthGrid__header");

        var prevBtn = _cwEl("button", "CalendarWidgetMonthGrid__navBtn");
        prevBtn.type = "button";
        prevBtn.setAttribute("aria-label", "Previous month");
        prevBtn.innerHTML = "&#x2039;";
        prevBtn.addEventListener("click", function () { navigateMonth(-1); });

        var monthLabel = _cwEl("div", "CalendarWidgetMonthGrid__monthLabel", displayMonth.format("MMMM YYYY"));

        var nextBtn = _cwEl("button", "CalendarWidgetMonthGrid__navBtn");
        nextBtn.type = "button";
        nextBtn.setAttribute("aria-label", "Next month");
        nextBtn.innerHTML = "&#x203A;";
        nextBtn.addEventListener("click", function () { navigateMonth(1); });

        header.appendChild(prevBtn);
        header.appendChild(monthLabel);
        header.appendChild(nextBtn);

        var weekdays = _cwEl("div", "CalendarWidgetMonthGrid__weekdays");
        for (var wi = 0; wi < 7; wi++) {
            weekdays.appendChild(_cwEl("div", "CalendarWidgetMonthGrid__weekday", weekdayLabels[wi]));
        }

        var days = _cwEl("div", "CalendarWidgetMonthGrid__days");
        var gridStart = displayMonth.clone().startOf("isoWeek");

        for (var i = 0; i < 42; i++) {
            var d = gridStart.clone().add(i, "days");
            var ymd = d.format("YYYY-MM-DD");

            var btn = _cwEl("button", "CalendarWidgetMonthGrid__day");
            btn.type = "button";
            btn.appendChild(_cwEl("span", "CalendarWidgetMonthGrid__dayLabel", String(d.date())));
            btn.setAttribute("data-ymd", ymd);
            btn.setAttribute("aria-label", d.format("dddd, MMMM D, YYYY"));

            if (d.month() !== displayMonth.month()) btn.classList.add("is-outside");
            if (ymd === todayYmd) btn.classList.add("is-today");
            if (ymd === chosenYmd) {
                btn.classList.add("is-selected");
                btn.setAttribute("aria-current", "date");
            }

            btn.addEventListener("click", function (ev) {
                var nextYmd = ev.currentTarget.getAttribute("data-ymd");
                if (nextYmd) setCalendarWidgetChosenDate(nextYmd);
            });

            days.appendChild(btn);
        }

        wrapper.appendChild(header);
        wrapper.appendChild(weekdays);
        wrapper.appendChild(days);
        el.appendChild(wrapper);

        var monthKey = displayMonth.format("YYYY-MM");
        fetchEventCounts(monthKey);
    }

    el._cwMonthGridRender = render;
    render();

    window.addEventListener("storage", function (e) {
        if (e.key === "calendarWidgetChosenDate") {
            try { render(); } catch (err) { /* a failed redraw keeps the previous grid */ }
        }
    });
}

function formatCalendarWidgetTimeRange(startIso, endIso) {
    if (!startIso) return "N/A";
    var opts = { hour: "2-digit", minute: "2-digit" };
    var s = new Date(startIso);
    if (isNaN(s.getTime())) return "N/A";
    var str = s.toLocaleTimeString(undefined, opts);
    if (!endIso) return str;
    var e = new Date(endIso);
    return isNaN(e.getTime()) ? str : str + " - " + e.toLocaleTimeString(undefined, opts);
}

function postCalendarEventDodge(eventId, dodge, asRecurring) {
    return fetch("/calendar/events/" + eventId + "/toggle_dodge/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dodge: dodge, as_recurring: asRecurring }),
        credentials: "same-origin",
    });
}

function showCalendarWidgetReprocessConfirm() {
    return new Promise(function (resolve) {
        var settled = false;
        function close(result) {
            if (settled) return;
            settled = true;
            document.removeEventListener("keydown", onKeydown, true);
            if (backdrop && backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
            resolve(result);
        }
        function onKeydown(e) {
            if (e.key === "Escape") {
                e.stopPropagation();
                close(false);
            } else if (e.key === "Enter") {
                e.stopPropagation();
                close(true);
            }
        }

        var backdrop = _cwEl("div", "CalendarWidgetConfirm__backdrop");
        backdrop.addEventListener("click", function (e) {
            if (e.target === backdrop) close(false);
        });

        var dialog = _cwEl("div", "CalendarWidgetConfirm");
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");
        dialog.setAttribute("aria-labelledby", "cwConfirmTitle");

        var title = _cwEl("h3", "CalendarWidgetConfirm__title", "Reprocess conversation?");
        title.id = "cwConfirmTitle";
        var text = _cwEl("p", "CalendarWidgetConfirm__text",
            "This conversation already took place and is currently generated. Changing the dialog type will reprocess it.");

        var actions = _cwEl("div", "CalendarWidgetConfirm__actions");
        var cancelBtn = _cwEl("button", "btn btn-light", "Cancel");
        cancelBtn.type = "button";
        cancelBtn.addEventListener("click", function () { close(false); });
        var okBtn = _cwEl("button", "btn btn-primary", "Continue");
        okBtn.type = "button";
        okBtn.addEventListener("click", function () { close(true); });

        actions.appendChild(cancelBtn);
        actions.appendChild(okBtn);

        dialog.appendChild(title);
        dialog.appendChild(text);
        dialog.appendChild(actions);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        document.addEventListener("keydown", onKeydown, true);
        setTimeout(function () { try { okBtn.focus(); } catch (e) { /* storage or focus unavailable: keep going */ } }, 0);
    });
}

var CW_BOT_STATUS_BUFFER_HOURS = 1;
var CW_BOT_STATUS_POLL_INTERVAL_MS = 10000;

var _cwConversationPopupEl = null;
var _cwConversationPopupAnchor = null;
var _cwConversationPopupConversationId = null;
var _cwRecurringPopupEl = null;
var _cwRecurringPopupAnchor = null;
var _cwRecurringPopupReject = null;
var _cwConversationPopupHandlersBound = false;
var _cwDialogTypesPromise = null;

function closeCalendarWidgetRecurringPopup(silent) {
    if (_cwRecurringPopupEl && _cwRecurringPopupEl.parentNode) {
        _cwRecurringPopupEl.parentNode.removeChild(_cwRecurringPopupEl);
    }
    if (!silent && _cwRecurringPopupReject) {
        _cwRecurringPopupReject(new Error("cancelled"));
    }
    _cwRecurringPopupEl = null;
    _cwRecurringPopupAnchor = null;
    _cwRecurringPopupReject = null;
}

function positionCalendarWidgetSidePopup(anchorEl, popupEl, preferredSide) {
    if (!anchorEl || !popupEl) return;
    var gap = 10;
    var viewportPadding = 12;
    var anchorRect = anchorEl.getBoundingClientRect();
    var popupRect = popupEl.getBoundingClientRect();
    var popupWidth = popupRect.width || 340;
    var popupHeight = popupRect.height || 220;

    var leftFirst = preferredSide === "left";
    var left = leftFirst ? anchorRect.left - popupWidth - gap : anchorRect.right + gap;
    if (left + popupWidth > window.innerWidth - viewportPadding) {
        left = anchorRect.left - popupWidth - gap;
    }
    if (left < viewportPadding) {
        left = anchorRect.right + gap;
    }
    if (left + popupWidth > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, window.innerWidth - popupWidth - viewportPadding);
    }

    var top = anchorRect.top;
    if (top + popupHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, window.innerHeight - popupHeight - viewportPadding);
    }

    popupEl.style.left = Math.round(left) + "px";
    popupEl.style.top = Math.round(top) + "px";
}

function showRecurringPrompt(article) {
    closeCalendarWidgetRecurringPopup(true);

    return new Promise(function (resolve, reject) {
        var popup = _cwEl("aside", "CalendarWidgetPopup CalendarWidgetRecurPopup");
        popup.setAttribute("role", "dialog");
        popup.setAttribute("aria-modal", "false");
        popup.setAttribute("aria-label", "Recurring event scope");

        var header = _cwEl("div", "CalendarWidgetPopup__header");
        header.appendChild(_cwEl("p", "CalendarWidgetPopup__title", "Recurring event"));

        var btnClose = _cwEl("button", "CalendarWidgetPopup__close");
        btnClose.type = "button";
        btnClose.setAttribute("aria-label", "Cancel");
        btnClose.innerHTML = "&times;";
        btnClose.addEventListener("click", function () {
            closeCalendarWidgetRecurringPopup(false);
        });
        header.appendChild(btnClose);

        var text = _cwEl("p", "CalendarWidgetPopup__text", "Apply this change to all recurring events?");
        var btns = _cwEl("div", "CalendarWidgetItem__recurPromptBtns");

        var btnAll = _cwEl("button", "btn btn-primary btn-sm", "All events");
        btnAll.type = "button";
        btnAll.addEventListener("click", function () {
            closeCalendarWidgetRecurringPopup(true);
            resolve(true);
        });

        var btnOne = _cwEl("button", "btn btn-light btn-sm", "Only this one");
        btnOne.type = "button";
        btnOne.addEventListener("click", function () {
            closeCalendarWidgetRecurringPopup(true);
            resolve(false);
        });

        btns.appendChild(btnOne);
        btns.appendChild(btnAll);

        popup.appendChild(header);
        popup.appendChild(text);
        popup.appendChild(btns);
        document.body.appendChild(popup);

        _cwRecurringPopupEl = popup;
        _cwRecurringPopupAnchor = article;
        _cwRecurringPopupReject = reject;
        positionCalendarWidgetSidePopup(article, popup, "left");
    });
}

function fetchCalendarWidgetDialogTypes() {
    if (_cwDialogTypesPromise) return _cwDialogTypesPromise;
    _cwDialogTypesPromise = fetch("/calendar/dialogtypes/", { credentials: "same-origin" })
        .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
        })
        .catch(function (err) {
            _cwDialogTypesPromise = null;
            throw err;
        });
    return _cwDialogTypesPromise;
}

function shouldIgnoreCalendarWidgetItemPopupClick(target) {
    if (!target || !target.closest) return false;
    return !!target.closest(
        ".CalendarWidgetItem__dialogType, .CalendarWidgetItem__toggle, .CalendarWidgetItem__recurPrompt, .CalendarWidgetPopup, .CalendarWidgetRecurPopup, #calendarWidgetUploadModal, button, input, label, a, select"
    );
}

var _cwUploadConversationId = null;
var _cwUploadPopupBody = null;
var _cwCalendarWidgetUploadInitialized = false;
var _cwCalendarWidgetUploadUrlPlaceholder = "00000000-0000-0000-0000-000000000000";

function isCalendarWidgetUploadEnabled() {
    return !!document.getElementById("calendarWidgetUploadModal");
}

var _cwCalendarWidgetUploadConfigCache = null;

function _cwCalendarWidgetSetBlockVisible(block, visible) {
    if (!block) return;
    block.classList.toggle("in:hidden", !visible);
}

function initCalendarWidgetDocumentUpload() {
    if (_cwCalendarWidgetUploadInitialized) return;
    var modalEl = document.getElementById("calendarWidgetUploadModal");
    var formEl = document.getElementById("calendarWidgetDocumentUploadForm");
    if (!modalEl || !formEl) return;
    _cwCalendarWidgetUploadInitialized = true;

    if (modalEl.parentNode !== document.body) {
        document.body.appendChild(modalEl);
    }

    var fileInput = document.getElementById("id_calendar_widget_document_file");
    var selectedBlock = document.getElementById("js-calendar-widget-document-upload-selected");
    var filenameEl = document.getElementById("js-calendar-widget-document-upload-filename");
    var loadingBlock = document.getElementById("js-calendar-widget-document-upload-loading");
    var errorBlock = document.getElementById("js-calendar-widget-document-upload-error");
    var errorMessageEl = document.getElementById("js-calendar-widget-document-upload-error-message");
    var formBlock = document.getElementById("js-calendar-widget-document-upload-form");
    var linkOptionBlock = document.getElementById("js-calendar-widget-document-upload-link-option");
    var submitButton = document.getElementById("js-calendar-widget-document-upload-submit");

    // The "or link a conversation" option belongs to the form: an upload
    // in flight (or its error) is the only thing left on screen.
    function setFormVisible(visible) {
        _cwCalendarWidgetSetBlockVisible(formBlock, visible);
        _cwCalendarWidgetSetBlockVisible(linkOptionBlock, visible);
    }
    var labelPicker = window.ContextLabelPicker.mount(
        document.getElementById("js-calendar-widget-document-upload-label-picker")
    );
    var uploadUrlTemplate = formEl.dataset.uploadUrlTemplate || "";
    var labelOptionsUrlTemplate = formEl.dataset.labelOptionsUrlTemplate || "";
    var uploadConfigUrl = formEl.dataset.uploadConfigUrl || "";
    var contextSourcesUrlTemplate = formEl.dataset.contextSourcesUrlTemplate || "";
    _cwDetachWidgetModal(modalEl);
    var linkModalEl = document.getElementById("calendarWidgetLinkModal");
    var linkOpenEl = document.getElementById("js-calendar-widget-link-open");
    _cwDetachWidgetModal(linkModalEl);
    var linkSearchEl = document.getElementById("js-calendar-widget-link-search");
    var linkListEl = document.getElementById("js-calendar-widget-link-list");
    var linkStatusEl = document.getElementById("js-calendar-widget-link-status");
    var linkPaginationEl = document.getElementById("js-calendar-widget-link-pagination");
    var linkSideEls = linkModalEl
        ? linkModalEl.querySelectorAll(".js-calendar-widget-link-side")
        : [];
    var linkPageSize = 5;
    // Sources usually happened before the conversation, so that side opens.
    var linkDefaultSide = "earlier";
    var linkState = { page: 1, query: "", side: linkDefaultSide, requestId: 0 };
    var linkSearchTimer = null;
    var maxUploadBytes = NaN;

    function fetchCalendarWidgetUploadConfig() {
        if (_cwCalendarWidgetUploadConfigCache) {
            return Promise.resolve(_cwCalendarWidgetUploadConfigCache);
        }
        if (!uploadConfigUrl) {
            return Promise.resolve({ max_upload_bytes: null });
        }
        return fetch(uploadConfigUrl, { credentials: "same-origin" })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error("Could not load upload settings.");
                }
                return response.json();
            })
            .then(function (payload) {
                _cwCalendarWidgetUploadConfigCache = {
                    max_upload_bytes: payload.max_upload_bytes,
                };
                return _cwCalendarWidgetUploadConfigCache;
            });
    }

    function refreshUploadPopupDetails() {
        return fetchCalendarWidgetConversationDetails(_cwUploadConversationId).then(
            function (details) {
                var popupBody = _cwUploadPopupBody;
                if (!popupBody || !details) return;
                renderCalendarWidgetConversationPopupDetails(popupBody, details);
                if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
                    positionCalendarWidgetConversationPopup(
                        _cwConversationPopupAnchor,
                        _cwConversationPopupEl
                    );
                }
            }
        );
    }

    function linkPickerEnabled() {
        return !!(linkModalEl && linkListEl && contextSourcesUrlTemplate);
    }

    function linkLabel(name) {
        return (linkModalEl && linkModalEl.dataset[name]) || "";
    }

    function contextSourcesUrl() {
        return contextSourcesUrlTemplate.replace(
            _cwCalendarWidgetUploadUrlPlaceholder,
            _cwUploadConversationId
        );
    }

    function setLinkStatus(text) {
        if (linkStatusEl) {
            linkStatusEl.textContent = text || "";
            _cwCalendarWidgetSetBlockVisible(linkStatusEl, !!text);
        }
    }

    function showLinkSide(side) {
        linkState.side = side;
        linkSideEls.forEach(function (button) {
            var active = button.dataset.side === side;
            button.setAttribute("aria-pressed", active ? "true" : "false");
            button.classList.toggle("in:bg-base-white", active);
            button.classList.toggle("in:text-base-black", active);
            button.classList.toggle("in:text-gray-500", !active);
        });
    }

    function resetLinkPicker() {
        if (!linkPickerEnabled()) return;
        linkState.page = 1;
        linkState.query = "";
        showLinkSide(linkDefaultSide);
        linkState.requestId += 1;
        if (linkSearchEl) linkSearchEl.value = "";
        linkListEl.innerHTML = "";
        if (linkPaginationEl) linkPaginationEl.innerHTML = "";
        setLinkStatus("");
    }

    function linkRowMeta(conv) {
        var parts = [];
        if (conv.took_place) {
            var parsed = new Date(conv.took_place);
            if (!isNaN(parsed.getTime())) {
                parts.push(
                    parsed.toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                    })
                );
            }
        }
        if (conv.status_label && conv.status !== "generated") {
            parts.push(conv.status_label);
        }
        return parts.join(" \u00b7 ");
    }

    function buildLinkRow(conv) {
        var row = _cwEl(
            "div",
            "in:flex in:items-center in:gap-3 in:py-3 in:min-w-0 in:border-b in:border-gray-100"
        );
        var main = _cwEl("div", "in:flex in:flex-col in:min-w-0 in:flex-1");
        main.appendChild(
            _cwEl(
                "span",
                "body-text-sm-medium in:text-base-black in:truncate",
                conv.contact || conv.title
            )
        );
        if (conv.contact) {
            main.appendChild(
                _cwEl("span", "body-text-xs-regular in:text-gray-500 in:truncate", conv.title)
            );
        }
        main.appendChild(
            _cwEl("span", "body-text-xs-regular in:text-gray-500 in:truncate", linkRowMeta(conv))
        );
        row.appendChild(main);

        if (!conv.selectable) {
            row.appendChild(
                _cwEl(
                    "span",
                    "body-text-xs-regular in:text-gray-500 in:shrink-0 in:text-right",
                    conv.reason || ""
                )
            );
            return row;
        }

        var addBtn = _cwEl(
            "button",
            "Button Button--Sm Button--OutlineGray in:shrink-0",
            linkLabel("labelAdd")
        );
        addBtn.type = "button";
        addBtn.addEventListener("click", function () {
            linkConversation(conv, addBtn, row);
        });
        row.appendChild(addBtn);
        return row;
    }

    function linkConversation(conv, button, row) {
        button.disabled = true;
        var body = new FormData();
        body.append("conversation_id", conv.id);
        fetch(contextSourcesUrl(), {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRFToken": _cwGetCsrfToken() },
            body: body,
            credentials: "same-origin",
        })
            .then(function (response) {
                return response.json().then(
                    function (payload) {
                        return { ok: response.ok, payload: payload };
                    },
                    function () {
                        return { ok: false, payload: { success: false } };
                    }
                );
            })
            .then(function (result) {
                if (!result.ok || !result.payload.success) {
                    throw new Error(
                        (result.payload && result.payload.message) ||
                            linkLabel("labelLinkError")
                    );
                }
                button.remove();
                row.appendChild(
                    _cwEl(
                        "span",
                        "body-text-xs-regular in:text-done-700 in:shrink-0",
                        linkLabel("labelAdded")
                    )
                );
                return refreshUploadPopupDetails();
            })
            .catch(function (error) {
                button.disabled = false;
                setLinkStatus(error.message || linkLabel("labelLinkError"));
            });
    }

    function linkPageNumbers(total, current) {
        var numbers = [];
        for (var page = 1; page <= total; page += 1) {
            var nearCurrent = Math.abs(page - current) <= 1;
            if (total <= 5 || page === 1 || page === total || nearCurrent) {
                numbers.push(page);
            }
        }
        return numbers;
    }

    function renderLinkPagination(payload) {
        if (!linkPaginationEl) return;
        linkPaginationEl.innerHTML = "";
        if (!payload.num_pages || payload.num_pages < 2) return;
        linkPageNumbers(payload.num_pages, payload.page).forEach(function (number) {
            var item = _cwEl("li", number === payload.page ? "active page-item" : "page-item");
            var link = _cwEl("button", "page-link", String(number));
            link.type = "button";
            link.addEventListener("click", function () {
                loadLinkCandidates(number);
            });
            item.appendChild(link);
            linkPaginationEl.appendChild(item);
        });
    }

    function loadLinkCandidates(page) {
        if (!linkPickerEnabled() || !_cwUploadConversationId) return;
        linkState.page = page || 1;
        var requestId = (linkState.requestId += 1);
        setLinkStatus(linkLabel("labelLoading"));

        var params = new URLSearchParams({
            limit: linkPageSize,
            page: linkState.page,
            q: linkState.query,
            side: linkState.side,
        });
        var url = contextSourcesUrl() + "?" + params.toString();

        fetch(url, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
        })
            .then(function (response) {
                var type = response.headers.get("content-type") || "";
                if (response.redirected || type.indexOf("application/json") === -1) {
                    throw new Error(linkLabel("labelSessionExpired"));
                }
                return response.json();
            })
            .then(function (payload) {
                if (requestId !== linkState.requestId) return;
                linkListEl.innerHTML = "";
                var conversations = Array.isArray(payload.conversations)
                    ? payload.conversations
                    : [];
                conversations.forEach(function (conv) {
                    linkListEl.appendChild(buildLinkRow(conv));
                });
                if (!linkListEl.childElementCount) {
                    if (linkPaginationEl) linkPaginationEl.innerHTML = "";
                    setLinkStatus(
                        payload.message ||
                            linkLabel(
                                linkState.side === "later" ? "labelEmptyLater" : "labelEmptyEarlier"
                            )
                    );
                    return;
                }
                setLinkStatus("");
                renderLinkPagination(payload);
            })
            .catch(function (error) {
                if (requestId !== linkState.requestId) return;
                if (linkPaginationEl) linkPaginationEl.innerHTML = "";
                setLinkStatus(error.message || linkLabel("labelLoadError"));
            });
    }

    showLinkSide(linkDefaultSide);
    linkSideEls.forEach(function (button) {
        button.addEventListener("click", function () {
            if (button.dataset.side === linkState.side) return;
            showLinkSide(button.dataset.side);
            loadLinkCandidates(1);
        });
    });

    if (linkSearchEl) {
        linkSearchEl.addEventListener("input", function () {
            if (linkSearchTimer) clearTimeout(linkSearchTimer);
            linkSearchTimer = setTimeout(function () {
                linkState.query = linkSearchEl.value.trim();
                loadLinkCandidates(1);
            }, 300);
        });
    }

    function resetUploadModal() {
        formEl.reset();
        _cwCalendarWidgetSetBlockVisible(selectedBlock, false);
        _cwCalendarWidgetSetBlockVisible(loadingBlock, false);
        _cwCalendarWidgetSetBlockVisible(errorBlock, false);
        setFormVisible(true);
        labelPicker.reset();
        if (filenameEl) filenameEl.textContent = "";
        if (errorMessageEl) errorMessageEl.textContent = "";
        if (submitButton) submitButton.disabled = false;
    }

    if (fileInput) {
        fileInput.addEventListener("change", function () {
            var file = fileInput.files && fileInput.files[0];
            _cwCalendarWidgetSetBlockVisible(errorBlock, false);
            if (!file || !selectedBlock || !filenameEl) {
                _cwCalendarWidgetSetBlockVisible(selectedBlock, false);
                return;
            }
            filenameEl.textContent = file.name;
            _cwCalendarWidgetSetBlockVisible(selectedBlock, true);
        });
    }

    formEl.addEventListener("submit", function (event) {
        event.preventDefault();
        var conversationId = _cwUploadConversationId;
        if (!conversationId) return;

        var file = fileInput.files && fileInput.files[0];
        if (!file) {
            fileInput.focus();
            return;
        }

        if (Number.isFinite(maxUploadBytes) && file.size > maxUploadBytes) {
            setFormVisible(true);
            _cwCalendarWidgetSetBlockVisible(loadingBlock, false);
            _cwCalendarWidgetSetBlockVisible(errorBlock, true);
            if (errorMessageEl) errorMessageEl.textContent = "File must be 10 MB or smaller.";
            return;
        }

        var contextLabelId = labelPicker.getSelectedId();
        if (!contextLabelId) {
            setFormVisible(true);
            _cwCalendarWidgetSetBlockVisible(loadingBlock, false);
            _cwCalendarWidgetSetBlockVisible(errorBlock, true);
            if (errorMessageEl) errorMessageEl.textContent = "Pick a label for this document.";
            return;
        }

        var formData = new FormData();
        formData.append("file", file);
        formData.append("context_label", contextLabelId);

        var uploadUrl = uploadUrlTemplate.replace(_cwCalendarWidgetUploadUrlPlaceholder, conversationId);

        setFormVisible(false);
        _cwCalendarWidgetSetBlockVisible(errorBlock, false);
        _cwCalendarWidgetSetBlockVisible(loadingBlock, true);
        if (submitButton) submitButton.disabled = true;

        fetch(uploadUrl, {
            method: "POST",
            headers: { "X-CSRFToken": _cwGetCsrfToken() },
            body: formData,
            credentials: "same-origin",
        })
            .then(function (response) {
                return response.json().then(
                    function (payload) {
                        return { ok: response.ok, payload: payload };
                    },
                    function () {
                        return {
                            ok: false,
                            payload: { success: false, message: "Failed to upload document. Please try again." },
                        };
                    }
                );
            })
            .then(function (result) {
                if (!result.ok || !result.payload.success) {
                    var message =
                        (result.payload && result.payload.message) ||
                        "Failed to upload document. Please try again.";
                    throw new Error(message);
                }
                if (window.ui) window.ui.modal.hide(modalEl);

                return refreshUploadPopupDetails();
            })
            .catch(function (error) {
                _cwCalendarWidgetSetBlockVisible(loadingBlock, false);
                setFormVisible(true);
                _cwCalendarWidgetSetBlockVisible(errorBlock, true);
                if (errorMessageEl) {
                    errorMessageEl.textContent =
                        error.message || "Failed to upload document. Please try again.";
                }
                if (submitButton) submitButton.disabled = false;
            });
    });

    modalEl.addEventListener("ui:modal:hidden", resetUploadModal);

    /* Bootstrap does not stack modals, so the picker waits for the Add
       source modal to finish hiding before it opens. */
    if (linkOpenEl && linkModalEl) {
        linkOpenEl.addEventListener("click", function () {
            if (!window.ui) return;
            var showPicker = function () {
                window.ui.modal.show(linkModalEl);
            };
            if (!modalEl.classList.contains("show")) {
                showPicker();
                return;
            }
            modalEl.addEventListener("ui:modal:hidden", function once() {
                modalEl.removeEventListener("ui:modal:hidden", once);
                showPicker();
            });
            window.ui.modal.hide(modalEl);
        });

        linkModalEl.addEventListener("ui:modal:shown", function () {
            _cwLiftWidgetModal(linkModalEl, 1092);
            if (linkSearchEl) linkSearchEl.focus();
            loadLinkCandidates(1);
        });

        linkModalEl.addEventListener("ui:modal:hidden", resetLinkPicker);
    }

    modalEl.addEventListener("ui:modal:shown", function () {
        _cwLiftWidgetModal(modalEl, 1090);
        if (fileInput) {
            fileInput.focus();
        }
    });

    window.openCalendarWidgetUploadModal = function (conversationId, popupBody) {
        _cwUploadConversationId = conversationId;
        _cwUploadPopupBody = popupBody;
        resetUploadModal();
        labelPicker.load(
            labelOptionsUrlTemplate.replace(_cwCalendarWidgetUploadUrlPlaceholder, conversationId)
        );

        if (!window.ui) return;
        window.ui.modal.show(modalEl);

        fetchCalendarWidgetUploadConfig()
            .then(function (config) {
                if (Number.isFinite(config.max_upload_bytes)) {
                    maxUploadBytes = config.max_upload_bytes;
                }
            })
            .catch(function () {});
    };
}

function bindCalendarWidgetPopupGlobalHandlers() {
    if (_cwConversationPopupHandlersBound) return;
    _cwConversationPopupHandlersBound = true;

    document.addEventListener("click", function (ev) {
        var target = ev.target;
        if (_cwRecurringPopupEl) {
            if (_cwRecurringPopupEl.contains(target)) return;
            if (_cwRecurringPopupAnchor && _cwRecurringPopupAnchor.contains(target)) return;
            closeCalendarWidgetRecurringPopup(false);
        }
        if (!_cwConversationPopupEl) return;
        if (_cwRecurringPopupEl && _cwRecurringPopupEl.contains(target)) return;
        var widgetModalIds = ["calendarWidgetUploadModal", "calendarWidgetLinkModal"];
        for (var mi = 0; mi < widgetModalIds.length; mi += 1) {
            var widgetModalEl = document.getElementById(widgetModalIds[mi]);
            if (widgetModalEl && widgetModalEl.classList.contains("show")) {
                if (target.closest("#" + widgetModalIds[mi]) || target.closest(".modal-backdrop")) {
                    return;
                }
            }
        }
        if (_cwConversationPopupEl.contains(target)) return;
        if (_cwConversationPopupAnchor && _cwConversationPopupAnchor.contains(target)) return;
        closeCalendarWidgetConversationPopup();
    });

    document.addEventListener("keydown", function (ev) {
        if (ev.key === "Escape") {
            closeCalendarWidgetRecurringPopup(false);
            closeCalendarWidgetConversationPopup();
        }
    });

    window.addEventListener("resize", function () {
        if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
            positionCalendarWidgetConversationPopup(_cwConversationPopupAnchor, _cwConversationPopupEl);
        }
        if (_cwRecurringPopupEl && _cwRecurringPopupAnchor) {
            positionCalendarWidgetSidePopup(_cwRecurringPopupAnchor, _cwRecurringPopupEl, "left");
        }
    });

    window.addEventListener("scroll", function () {
        if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
            positionCalendarWidgetConversationPopup(_cwConversationPopupAnchor, _cwConversationPopupEl);
        }
        if (_cwRecurringPopupEl && _cwRecurringPopupAnchor) {
            positionCalendarWidgetSidePopup(_cwRecurringPopupAnchor, _cwRecurringPopupEl, "left");
        }
    }, true);
}

function positionCalendarWidgetConversationPopup(anchorEl, popupEl) {
    positionCalendarWidgetSidePopup(anchorEl, popupEl, "right");
}

function closeCalendarWidgetConversationPopup() {
    if (_cwConversationPopupEl && _cwConversationPopupEl.parentNode) {
        _cwConversationPopupEl.parentNode.removeChild(_cwConversationPopupEl);
    }
    _cwConversationPopupEl = null;
    _cwConversationPopupAnchor = null;
    _cwConversationPopupConversationId = null;
}

function renderCalendarWidgetConversationPopupLoading(popupBody) {
    popupBody.innerHTML = "";
    popupBody.appendChild(_cwEl("p", "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted", "Loading conversation details\u2026"));
}

function renderCalendarWidgetConversationPopupError(popupBody) {
    popupBody.innerHTML = "";
    popupBody.appendChild(_cwEl("p", "CalendarWidgetPopup__text CalendarWidgetPopup__text--error", "Could not load conversation details."));
}

function _cwParticipantDisplayName(participant) {
    if (participant.candidate) {
        var c = participant.candidate;
        var name = [c.first_name, c.last_name].filter(Boolean).join(" ");
        return name || c.email || "Unknown candidate";
    }
    if (participant.user) {
        var u = participant.user;
        return [u.first_name, u.last_name].filter(Boolean).join(" ") || "Unknown user";
    }
    return "Unknown participant";
}

function _cwParticipantRole(participant) {
    if (participant.candidate) return "candidate";
    if (participant.user) return "user";
    return "";
}

function _cwFormatDatetime(iso) {
    if (!iso) return null;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function buildCalendarWidgetDialogTypeControl(conv, dialogTypes, calendarEventId) {
    var wrap = _cwEl("div", "CalendarWidgetItem__dialogType");
    var selectId = "cwDialogType_" + String(conv.id);
    var label = _cwEl("label", "CalendarWidgetItem__dialogTypeLabel", "Dialog type");
    label.setAttribute("for", selectId);
    var select = document.createElement("select");
    select.id = selectId;
    select.className = "CalendarWidgetItem__dialogTypeSelect";
    var isConversationProcessing = _cwIsConversationProcessing(conv && conv.status);

    var placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "\u2014";
    select.appendChild(placeholder);

    var currentId = conv.dialogtype_id ? String(conv.dialogtype_id) : "";
    var list = dialogTypes || [];
    for (var i = 0; i < list.length; i++) {
        var dt = list[i];
        if (!dt || dt.id == null) continue;
        var opt = document.createElement("option");
        opt.value = String(dt.id);
        opt.textContent = dt.name || "(unnamed)";
        if (opt.value === currentId) opt.selected = true;
        select.appendChild(opt);
    }
    if (!currentId) placeholder.selected = true;

    if (isConversationProcessing) {
        select.disabled = true;
        select.title = "Unavailable while conversation is generating.";
    }
    var ev = conv.calendar_event;
    if (ev && !ev.is_single_organisation) {
        select.disabled = true;
        select.title = "Unavailable for non-single organisation conversations.";
    }
    if (!calendarEventId) {
        select.disabled = true;
    }

    var error = _cwEl("p", "CalendarWidgetItem__dialogTypeError");
    error.style.display = "none";

    var eventId = calendarEventId;

    select.addEventListener("change", function () {
        if (isConversationProcessing) return;
        if (!eventId) return;
        var newId = select.value;
        if (!newId) {
            select.value = currentId;
            return;
        }
        if (newId === currentId) return;

        var previousId = currentId;
        select.disabled = true;
        error.style.display = "none";

        var confirmReprocess = (conv && conv.status && _cwIsConversationGenerated(conv.status) && conv.took_place)
            ? showCalendarWidgetReprocessConfirm()
            : Promise.resolve(true);

        confirmReprocess.then(function (ok) {
            if (!ok) {
                select.value = previousId;
                select.disabled = isConversationProcessing || !eventId;
                return;
            }

            return fetch("/calendar/conversation/" + encodeURIComponent(eventId) + "/set_dialogtype/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": _cwGetCsrfToken(),
                },
                credentials: "same-origin",
                body: JSON.stringify({ dialogtype_id: newId }),
            })
                .then(function (res) {
                    if (!res.ok) throw new Error("HTTP " + res.status);
                    return res.json().catch(function () { return {}; });
                })
                .then(function () {
                    currentId = newId;
                    conv.dialogtype_id = newId;
                })
                .catch(function () {
                    select.value = previousId;
                    error.textContent = "Could not update dialog type.";
                    error.style.display = "";
                })
                .then(function () {
                    select.disabled = isConversationProcessing || !eventId;
                });
        });
    });

    wrap.appendChild(label);
    wrap.appendChild(select);
    wrap.appendChild(error);
    return wrap;
}

function _cwRefreshDocumentsPopup(conversationId, popupBody) {
    return fetchCalendarWidgetConversationDetails(conversationId).then(function (details) {
        if (!popupBody || !details) return;
        renderCalendarWidgetConversationPopupDetails(popupBody, details);
        if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
            positionCalendarWidgetConversationPopup(_cwConversationPopupAnchor, _cwConversationPopupEl);
        }
    });
}

function _cwDocumentActionMessage(docLi, text, isError) {
    var existing = docLi.querySelector(".CalendarWidgetPopup__documentNotice");
    if (existing) existing.parentNode.removeChild(existing);
    if (!text) return null;
    var notice = _cwEl(
        "p",
        "CalendarWidgetPopup__documentNotice" +
            (isError ? " CalendarWidgetPopup__documentNotice--error" : ""),
        text
    );
    docLi.appendChild(notice);
    return notice;
}

function _cwDocumentUsageText(usage) {
    if (!usage) return "";
    var parts = [];
    var artifacts = usage.artifacts || [];
    if (artifacts.length) {
        parts.push("documents created in chat (" + artifacts.join(", ") + ")");
    }
    if ((usage.chat_answers || 0) + (usage.qa_exchanges || 0) > 0) {
        parts.push("answers the assistant has already given");
    }
    if (usage.report) {
        parts.push("the generated report");
    }
    return parts.join(", ");
}

// The popup is a hand-positioned aside that already fights Bootstrap's
// stacking order, so confirmations and blockers render inline here rather
// than in a nested modal.
function _cwPostDocumentAction(options) {
    var button = options.button;
    if (button.disabled) return;
    button.disabled = true;
    _cwDocumentActionMessage(options.docLi, "");

    fetch(options.url, {
        method: "POST",
        headers: {
            "X-CSRFToken": _cwGetCsrfToken(),
            "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
    })
        .then(function (response) {
            var contentType = response.headers.get("Content-Type") || "";
            if (response.redirected || contentType.indexOf("application/json") === -1) {
                window.location.href = response.url;
                return { handled: true };
            }
            return response.json().then(
                function (payload) {
                    return { ok: response.ok, status: response.status, payload: payload };
                },
                function () {
                    return { ok: false, status: response.status, payload: {} };
                }
            );
        })
        .then(function (result) {
            if (result.handled) return;
            if (result.ok && result.payload.success) {
                return _cwRefreshDocumentsPopup(options.conversationId, options.popupBody);
            }
            button.disabled = false;
            if (result.status === 409 && options.onConflict) {
                options.onConflict(result.payload);
                return;
            }
            // Anything else may mean the document is already gone: refresh so
            // the popup stops showing buttons for it.
            _cwDocumentActionMessage(
                options.docLi,
                result.payload.message || "Something went wrong. Please try again.",
                true
            );
        })
        .catch(function () {
            button.disabled = false;
            _cwDocumentActionMessage(
                options.docLi,
                "Something went wrong. Please try again.",
                true
            );
        });
}

function _cwBuildDocumentActionButton(options) {
    var button = _cwEl("button", "CalendarWidgetPopup__documentLink", options.label);
    button.type = "button";
    button.addEventListener("click", function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        _cwPostDocumentAction({
            button: button,
            url: options.url,
            conversationId: options.conversationId,
            popupBody: options.popupBody,
            docLi: options.docLi,
        });
    });
    return button;
}

function _cwBuildDocumentRemoveButton(options) {
    var button = _cwEl(
        "button",
        "CalendarWidgetPopup__documentLink CalendarWidgetPopup__documentLink--danger",
        "Remove"
    );
    button.type = "button";
    var awaitingConfirm = false;

    function reset() {
        awaitingConfirm = false;
        button.textContent = "Remove";
    }

    button.addEventListener("click", function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        if (!awaitingConfirm) {
            awaitingConfirm = true;
            button.textContent = "Confirm remove";
            _cwDocumentActionMessage(options.docLi, "Click again to remove this document.");
            return;
        }
        reset();
        _cwPostDocumentAction({
            button: button,
            url: options.url,
            conversationId: options.conversationId,
            popupBody: options.popupBody,
            docLi: options.docLi,
            onConflict: function (payload) {
                var usage = _cwDocumentUsageText(payload.usage);
                _cwDocumentActionMessage(
                    options.docLi,
                    "Cannot remove: already used by " +
                        (usage || "this conversation") +
                        (options.archiveUrl ? ". Archive it instead." : "."),
                    true
                );
            },
        });
    });
    return button;
}

function renderCalendarWidgetConversationPopupDetails(popupBody, details) {
    popupBody.innerHTML = "";

    var isConversationProcessing = _cwIsConversationProcessing(details && details.status);
    var badgesWrap = _cwEl("div", "CalendarWidgetPopup__badges");
    if (details.status) {
        badgesWrap.appendChild(_cwEl("span", "CalendarWidgetPopup__badge CalendarWidgetPopup__badge--status", details.status));
    }
    if (badgesWrap.childNodes.length) popupBody.appendChild(badgesWrap);

    var rows = [
        ["Platform", details.platform || "N/A"],
        ["Last update", _cwFormatDatetime(details.updated) || "N/A"],
    ];

    var dl = _cwEl("dl", "CalendarWidgetPopup__list");
    for (var i = 0; i < rows.length; i++) {
        var rowWrap = _cwEl("div", "CalendarWidgetPopup__row");
        rowWrap.appendChild(_cwEl("dt", "CalendarWidgetPopup__term", rows[i][0]));
        rowWrap.appendChild(_cwEl("dd", "CalendarWidgetPopup__value", rows[i][1]));
        dl.appendChild(rowWrap);
    }
    popupBody.appendChild(dl);

    var participants = Array.isArray(details.participants) ? details.participants : [];
    if (participants.length) {
        var participantsWrap = _cwEl("div", "CalendarWidgetPopup__participants");
        participantsWrap.appendChild(_cwEl("p", "CalendarWidgetPopup__subTitle", "Participants"));
        var pList = _cwEl("ul", "CalendarWidgetPopup__participantList");
        for (var pi = 0; pi < participants.length; pi++) {
            var p = participants[pi];
            var li = _cwEl("li", "CalendarWidgetPopup__participant");
            var nameEl = _cwEl("span", "CalendarWidgetPopup__participantName", _cwParticipantDisplayName(p));
            li.appendChild(nameEl);
            var role = _cwParticipantRole(p);
            if (role) {
                li.appendChild(_cwEl("span", "CalendarWidgetPopup__participantRole", role));
            }
            var email = (p.candidate && p.candidate.email) || (p.user && p.user.email);
            if (email) {
                li.appendChild(_cwEl("span", "CalendarWidgetPopup__participantEmail", email));
            }
            pList.appendChild(li);
        }
        participantsWrap.appendChild(pList);
        popupBody.appendChild(participantsWrap);
    }

    var hasCandidate = participants.some(function (p) { return !!p.candidate; });
    if (details.took_place && !hasCandidate) {
        var assignWrap = _cwEl("div", "CalendarWidgetPopup__assign");
        var assignBtn = _cwEl("button", "CalendarWidgetPopup__assignBtn", "+ Add contact");
        assignBtn.type = "button";
        if (isConversationProcessing) {
            assignBtn.disabled = true;
            assignBtn.title = "Unavailable while conversation is generating.";
        }

        var assignForm = _cwEl("form", "CalendarWidgetPopup__assignForm");
        assignForm.style.display = "none";

        var fNameInput = document.createElement("input");
        fNameInput.type = "text";
        fNameInput.name = "first_name";
        fNameInput.placeholder = "First name";
        fNameInput.required = true;
        fNameInput.className = "CalendarWidgetPopup__input";

        var lNameInput = document.createElement("input");
        lNameInput.type = "text";
        lNameInput.name = "last_name";
        lNameInput.placeholder = "Last name";
        lNameInput.required = true;
        lNameInput.className = "CalendarWidgetPopup__input";

        var emailInput = document.createElement("input");
        emailInput.type = "email";
        emailInput.name = "email";
        emailInput.placeholder = "Email";
        emailInput.required = true;
        emailInput.className = "CalendarWidgetPopup__input";

        var phoneInput = document.createElement("input");
        phoneInput.type = "tel";
        phoneInput.name = "phone";
        phoneInput.placeholder = "Mobile phone (optional)";
        phoneInput.className = "CalendarWidgetPopup__input";

        var submitBtn = _cwEl("button", "CalendarWidgetPopup__assignSubmit", "Add");
        submitBtn.type = "submit";

        var cancelBtn = _cwEl("button", "CalendarWidgetPopup__assignCancel", "Cancel");
        cancelBtn.type = "button";

        var formBtns = _cwEl("div", "CalendarWidgetPopup__assignActions");
        formBtns.appendChild(cancelBtn);
        formBtns.appendChild(submitBtn);

        assignForm.appendChild(fNameInput);
        assignForm.appendChild(lNameInput);
        assignForm.appendChild(emailInput);
        assignForm.appendChild(phoneInput);
        assignForm.appendChild(formBtns);

        var errorEl = _cwEl("p", "CalendarWidgetPopup__assignError");
        errorEl.style.display = "none";

        assignBtn.addEventListener("click", function () {
            if (isConversationProcessing) return;
            assignBtn.style.display = "none";
            assignForm.style.display = "";
            fNameInput.focus();

            requestAnimationFrame(function () {
                if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
                    positionCalendarWidgetConversationPopup(_cwConversationPopupAnchor, _cwConversationPopupEl);
                }
                assignForm.scrollIntoView({ block: "nearest" });
                fNameInput.focus({ preventScroll: true });
            });
        });

        cancelBtn.addEventListener("click", function () {
            assignForm.style.display = "none";
            assignBtn.style.display = "";
            errorEl.style.display = "none";

            requestAnimationFrame(function () {
                if (_cwConversationPopupEl && _cwConversationPopupAnchor) {
                    positionCalendarWidgetConversationPopup(_cwConversationPopupAnchor, _cwConversationPopupEl);
                }
                assignForm.scrollIntoView({ block: "nearest" });
                fNameInput.focus({ preventScroll: true });
            });
        });

        assignForm.addEventListener("submit", function (ev) {
            ev.preventDefault();
            if (isConversationProcessing) return;
            submitBtn.disabled = true;
            errorEl.style.display = "none";

            var conversationId = details.id;
            fetch("/calendar/widget/conversations/" + encodeURIComponent(conversationId) + "/assign-candidate/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": _cwGetCsrfToken(),
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    first_name: fNameInput.value.trim(),
                    last_name: lNameInput.value.trim(),
                    email: emailInput.value.trim(),
                    phone: phoneInput.value.trim(),
                }),
            })
            .then(function (res) {
                if (!res.ok) return res.json().then(function (d) {
                    var msg = d && (d.detail || d.email || d.first_name || d.last_name);
                    throw new Error(Array.isArray(msg) ? msg[0] : (msg || "Error"));
                });
                return res.json();
            })
            .then(function (updatedParticipants) {
                details.participants = updatedParticipants;
                renderCalendarWidgetConversationPopupDetails(popupBody, details);
            })
            .catch(function (err) {
                errorEl.textContent = err.message || "Could not assign candidate.";
                errorEl.style.display = "";
                submitBtn.disabled = false;
            });
        });

        assignWrap.appendChild(assignBtn);
        assignWrap.appendChild(assignForm);
        assignWrap.appendChild(errorEl);
        popupBody.appendChild(assignWrap);
    }

    var documents = Array.isArray(details.documents) ? details.documents : [];
    var documentsWrap = _cwEl("div", "CalendarWidgetPopup__documents");
    var documentsHeader = _cwEl("div", "CalendarWidgetPopup__documentsHeader");
    documentsHeader.appendChild(_cwEl("p", "CalendarWidgetPopup__subTitle", "Documents"));
    if (isCalendarWidgetUploadEnabled()) {
        var uploadBtn = _cwEl("button", "CalendarWidgetPopup__uploadBtn");
        uploadBtn.type = "button";
        var uploadIcon = document.createElement("i");
        uploadIcon.className = "ri-upload-2-line";
        uploadIcon.setAttribute("aria-hidden", "true");
        uploadBtn.appendChild(uploadIcon);
        uploadBtn.appendChild(document.createTextNode("Add source"));
        uploadBtn.addEventListener("click", function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            if (window.openCalendarWidgetUploadModal) {
                window.openCalendarWidgetUploadModal(details.id, popupBody);
            }
        });
        documentsHeader.appendChild(uploadBtn);
    }
    documentsWrap.appendChild(documentsHeader);

    if (documents.length) {
        var docList = _cwEl("ul", "CalendarWidgetPopup__documentList");
        for (var di = 0; di < documents.length; di++) {
            var doc = documents[di];
            var docLi = _cwEl("li", "CalendarWidgetPopup__document");
            var docMain = _cwEl("div", "CalendarWidgetPopup__documentMain");
            docMain.appendChild(_cwEl("span", "CalendarWidgetPopup__documentTitle", doc.title || "Untitled"));

            var docMeta = _cwEl("div", "CalendarWidgetPopup__documentMeta");
            if (doc.context_label) {
                docMeta.appendChild(_cwEl("span", "CalendarWidgetPopup__documentLabel", doc.context_label));
            }
            var statusText = doc.status ? String(doc.status) : "";
            if (statusText) {
                var statusClass = "CalendarWidgetPopup__documentStatus";
                var statusLower = statusText.toLowerCase();
                if (statusLower === "active") {
                    statusClass += " CalendarWidgetPopup__documentStatus--active";
                } else if (statusLower === "error") {
                    statusClass += " CalendarWidgetPopup__documentStatus--error";
                } else if (statusLower === "archived") {
                    statusClass += " CalendarWidgetPopup__documentStatus--archived";
                } else {
                    statusClass += " CalendarWidgetPopup__documentStatus--inactive";
                }
                docMeta.appendChild(_cwEl("span", statusClass, statusText));
            }
            docMain.appendChild(docMeta);
            var docStatusLower = String(doc.status || "").toLowerCase();
            if (docStatusLower === "inactive") {
                docMain.appendChild(_cwEl(
                    "p",
                    "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted",
                    "Processing. This document becomes available when parsing finishes."
                ));
            } else if (docStatusLower === "error" && doc.error_message) {
                docMain.appendChild(_cwEl(
                    "p",
                    "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted",
                    String(doc.error_message)
                ));
            } else if (doc.has_chunks === false) {
                docMain.appendChild(_cwEl(
                    "p",
                    "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted",
                    "Search chunks for this document are not available yet."
                ));
            }
            docLi.appendChild(docMain);

            var docActions = _cwEl("div", "CalendarWidgetPopup__documentActions");
            if (doc.show_url) {
                var showLink = document.createElement("a");
                showLink.className = "CalendarWidgetPopup__documentLink";
                showLink.href = doc.show_url;
                showLink.target = "_blank";
                showLink.rel = "noopener noreferrer";
                showLink.textContent = "Show";
                docActions.appendChild(showLink);
            }
            if (doc.source_document_url) {
                var sourceLink = document.createElement("a");
                sourceLink.className = "CalendarWidgetPopup__documentLink";
                sourceLink.href = doc.source_document_url;
                sourceLink.target = "_blank";
                sourceLink.rel = "noopener noreferrer";
                sourceLink.textContent = "Source document";
                docActions.appendChild(sourceLink);
            }
            var archiveUrl = doc.archive_url || doc.unarchive_url;
            if (archiveUrl) {
                docActions.appendChild(_cwBuildDocumentActionButton({
                    label: doc.archive_url ? "Archive" : "Unarchive",
                    url: archiveUrl,
                    conversationId: details.id,
                    popupBody: popupBody,
                    docLi: docLi,
                }));
            }
            // Reprocess a failed upload on the same row instead of
            // uploading the file again (which duplicated documents).
            if (doc.retry_url) {
                docActions.appendChild(_cwBuildDocumentActionButton({
                    label: "Retry",
                    url: doc.retry_url,
                    conversationId: details.id,
                    popupBody: popupBody,
                    docLi: docLi,
                }));
            }
            if (doc.remove_url) {
                docActions.appendChild(_cwBuildDocumentRemoveButton({
                    url: doc.remove_url,
                    archiveUrl: doc.archive_url,
                    conversationId: details.id,
                    popupBody: popupBody,
                    docLi: docLi,
                }));
            }
            if (docActions.childElementCount > 0) {
                docLi.appendChild(docActions);
            }

            docList.appendChild(docLi);
        }
        documentsWrap.appendChild(docList);
    } else {
        documentsWrap.appendChild(_cwEl("p", "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted", "No documents"));
    }

    popupBody.appendChild(documentsWrap);

    var labels = Array.isArray(details.labels) ? details.labels : [];
    if (labels.length) {
        var labelsWrap = _cwEl("div", "CalendarWidgetPopup__labels");
        labelsWrap.appendChild(_cwEl("p", "CalendarWidgetPopup__subTitle", "Labels"));
        var list = _cwEl("div", "CalendarWidgetPopup__pillList");
        for (var labelIndex = 0; labelIndex < labels.length; labelIndex++) {
            list.appendChild(_cwEl("span", "CalendarWidgetPopup__pill", String(labels[labelIndex])));
        }
        labelsWrap.appendChild(list);
        popupBody.appendChild(labelsWrap);
    }

    if (isConversationProcessing) {
        var disabledNote = _cwEl("p", "CalendarWidgetPopup__text CalendarWidgetPopup__text--muted", "Actions are unavailable while this conversation is generating.");
        popupBody.appendChild(disabledNote);
    }
}

function buildCalendarWidgetConversationPopup(anchorEl, conv) {
    var popup = _cwEl("aside", "CalendarWidgetPopup");
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "false");
    popup.setAttribute("aria-label", "Conversation details");

    var head = _cwEl("div", "CalendarWidgetPopup__header");
    head.appendChild(_cwEl("p", "CalendarWidgetPopup__title", "Conversation details"));
    var closeBtn = _cwEl("button", "CalendarWidgetPopup__close");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", function () {
        closeCalendarWidgetConversationPopup();
    });
    head.appendChild(closeBtn);

    var subtitle = _cwEl("p", "CalendarWidgetPopup__subtitle", conv && conv.calendar_event && conv.calendar_event.title ? conv.calendar_event.title : "Conversation");
    var body = _cwEl("div", "CalendarWidgetPopup__body");

    popup.appendChild(head);
    popup.appendChild(subtitle);
    popup.appendChild(body);
    document.body.appendChild(popup);

    _cwConversationPopupEl = popup;
    _cwConversationPopupAnchor = anchorEl;
    _cwConversationPopupConversationId = conv && conv.id ? conv.id : null;
    renderCalendarWidgetConversationPopupLoading(body);
    positionCalendarWidgetConversationPopup(anchorEl, popup);
    return body;
}

function fetchCalendarWidgetConversationDetails(conversationId) {
    var url = "/calendar/widget/conversations/" + encodeURIComponent(conversationId) + "/details/";
    var dateStr = getCalendarWidgetChosenDateYmd();
    if (dateStr) url += "?date=" + encodeURIComponent(dateStr);
    return fetch(url, { credentials: "same-origin" })
        .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
        });
}

function openCalendarWidgetConversationPopup(anchorEl, conv) {
    closeCalendarWidgetRecurringPopup(true);
    closeCalendarWidgetConversationPopup();
    bindCalendarWidgetPopupGlobalHandlers();
    var popupBody = buildCalendarWidgetConversationPopup(anchorEl, conv);

    fetchCalendarWidgetConversationDetails(conv.id)
        .then(function (details) {
            if (!_cwConversationPopupEl) return;
            if (_cwConversationPopupConversationId !== conv.id) return;

            var fallbackEventId = conv && conv.calendar_event && conv.calendar_event.id ? conv.calendar_event.id : null;

            var payload = {
                id: details && details.id != null ? details.id : conv.id,
                status: details && details.status ? details.status : (conv.status || "N/A"),
                platform: details && details.platform ? details.platform : (conv.platform || "N/A"),
                took_place: details && details.took_place ? details.took_place : (conv.took_place || null),
                participants: Array.isArray(details.participants) ? details.participants : [],
                updated: details && details.updated ? details.updated : (conv.updated || "N/A"),
                labels: details && Array.isArray(details.labels) ? details.labels : (conv.labels || []),
                calendar_event_id: details && details.calendar_event_id ? details.calendar_event_id : fallbackEventId,
                documents: details && Array.isArray(details.documents) ? details.documents : [],
            };
            renderCalendarWidgetConversationPopupDetails(popupBody, payload);
            positionCalendarWidgetConversationPopup(anchorEl, _cwConversationPopupEl);
        })
        .catch(function () {
            if (!_cwConversationPopupEl) return;
            renderCalendarWidgetConversationPopupError(popupBody);
            positionCalendarWidgetConversationPopup(anchorEl, _cwConversationPopupEl);
        });
}

function _cwIsInBotStatusWindow(conv) {
    var ev = conv && conv.calendar_event;
    if (!ev || !ev.start_time) return false;
    var url = ev.url ? String(ev.url).trim() : "";
    if (!url) return false;
    var bufferMs = CW_BOT_STATUS_BUFFER_HOURS * 60 * 60 * 1000;
    var now = Date.now();
    var start = new Date(ev.start_time).getTime();
    var end = ev.end_time ? new Date(ev.end_time).getTime() : start;
    return !isNaN(start) && now >= start - bufferMs && now <= end + bufferMs;
}

function _cwShouldShowBotStatus(conv) {
    return _cwIsInBotStatusWindow(conv);
}

var _CW_BOT_ERROR_STATUSES = [
    "Error: Cannot Join", "Error: Timed Out", "Bot Not Accepted", "Bot Removed",
    "Error: Internal", "Error: Invalid URL", "Failed", "Timed Out in Waiting Room",
    "Recording Failed", "Malformed Logs",
    // MeetingBaas API v2
    "Bot Rejected", "Bot Removed Before Recording Start", "Bot Removed via API",
    "Error: Invalid Meeting URL", "Error: Meeting Error", "Error: Not Enough Tokens",
    "Error: Bot Creation Limit Reached", "Error: Transcription Error",
];

function _cwBotStatusGroup(statusStr) {
    var s = String(statusStr || "");
    // The bot is admitted to a waiting room and needs a host/participant to
    // let it in — surfaced distinctly so the user knows to admit it.
    if (s === "In Waiting Room" || s === "In Waiting Room For Host") {
        return "waiting_room";
    }
    // v2 adds Queued / Pickup Delayed (pre-call).
    if (
        s === "Queued" || s === "Pickup Delayed" ||
        s === "Joining Call"
    ) return "joining";
    // v2 adds Recording Paused / Resumed and post-call Transcribing.
    if (
        s === "In Call" || s === "Recording" ||
        s === "Recording Paused" || s === "Recording Resumed" ||
        s === "Transcribing"
    ) return "in_meeting";
    if (s === "Complete") return "complete";
    if (_CW_BOT_ERROR_STATUSES.indexOf(s) !== -1) return "error";
    return "idle";
}

function _cwBuildBotStatusDom() {
    var wrap = _cwEl("div", "CalendarWidgetItem__botStatus");
    var row = _cwEl("div", "CalendarWidgetItem__botStatusRow");
    var iconEl = _cwEl("span", "CalendarWidgetItem__botStatusIcon");
    var textEl = _cwEl("span", "CalendarWidgetItem__botStatusText", "Checking notetaker\u2026");
    var joinBtn = _cwEl("button", "CalendarWidgetItem__joinBtn");
    joinBtn.type = "button";
    joinBtn.textContent = "Join notetaker";
    joinBtn.style.display = "none";

    var recDot = _cwEl("span", "cw-rec-dot");
    recDot.setAttribute("aria-hidden", "true");

    row.appendChild(iconEl);
    row.appendChild(textEl);
    wrap.appendChild(row);
    wrap.appendChild(joinBtn);

    return { wrap: wrap, iconEl: iconEl, textEl: textEl, joinBtn: joinBtn, recDot: recDot };
}

function _cwRenderBotStatusJoinFailed(els) {
    els.wrap.style.display = "";
    els.iconEl.innerHTML = "";
    els.iconEl.className = "CalendarWidgetItem__botStatusIcon CalendarWidgetItem__botStatusIcon--error";
    var failIcon = _cwEl("i", "ri-close-circle-line");
    failIcon.setAttribute("aria-hidden", "true");
    els.iconEl.appendChild(failIcon);
    els.textEl.textContent = "Notetaker could not join";
    els.joinBtn.disabled = false;
    els.joinBtn.style.display = "";
}

function _cwRenderBotStatus(els, rawStatus) {
    var group = _cwBotStatusGroup(rawStatus);

    els.wrap.style.display = "";
    els.iconEl.innerHTML = "";
    els.iconEl.className = "CalendarWidgetItem__botStatusIcon";

    if (group === "joining") {
        els.iconEl.classList.add("CalendarWidgetItem__botStatusIcon--joining");
        var spinIcon = _cwEl("i", "ri-loader-4-line cw-spin");
        spinIcon.setAttribute("aria-hidden", "true");
        els.iconEl.appendChild(spinIcon);
        els.textEl.textContent = "Notetaker is joining\u2026";
        els.joinBtn.style.display = "none";
    } else if (group === "waiting_room") {
        els.iconEl.classList.add("CalendarWidgetItem__botStatusIcon--waiting");
        var waitIcon = _cwEl("i", "ri-time-line");
        waitIcon.setAttribute("aria-hidden", "true");
        els.iconEl.appendChild(waitIcon);
        els.textEl.textContent = "Notetaker is in the waiting room";
        els.joinBtn.style.display = "none";
    } else if (group === "in_meeting") {
        els.iconEl.classList.add("CalendarWidgetItem__botStatusIcon--in-meeting");
        els.iconEl.appendChild(els.recDot);
        els.textEl.textContent = "Notetaker is in the meeting";
        els.joinBtn.style.display = "none";
    } else if (group === "complete") {
        els.wrap.style.display = "none";
    } else if (group === "error") {
        els.iconEl.classList.add("CalendarWidgetItem__botStatusIcon--error");
        var errIcon = _cwEl("i", "ri-close-circle-line");
        errIcon.setAttribute("aria-hidden", "true");
        els.iconEl.appendChild(errIcon);
        els.textEl.textContent = rawStatus;
        els.joinBtn.disabled = false;
        els.joinBtn.style.display = "";
    } else {
        els.iconEl.classList.add("CalendarWidgetItem__botStatusIcon--idle");
        var warnIcon = _cwEl("i", "ri-error-warning-line");
        warnIcon.setAttribute("aria-hidden", "true");
        els.iconEl.appendChild(warnIcon);
        els.textEl.textContent = "Notetaker not in meeting";
        els.joinBtn.disabled = false;
        els.joinBtn.style.display = "";
    }
}

function fetchCalendarWidgetBotStatus(conversationId) {
    return fetch("/calendar/widget/conversations/" + encodeURIComponent(conversationId) + "/check-bot-status/", {
        credentials: "same-origin",
    })
        .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
        })
        .then(function (data) {
            return data && data.status ? data.status : "Unknown";
        });
}

function postCalendarWidgetJoinNotetaker(conversationId) {
    return fetch("/calendar/widget/conversations/" + encodeURIComponent(conversationId) + "/join-notetaker/", {
        method: "POST",
        headers: { "X-CSRFToken": _cwGetCsrfToken() },
        credentials: "same-origin",
    })
        .then(function (res) {
            if (!res.ok) {
                return res.json().catch(function () { return {}; }).then(function () {
                    throw new Error("HTTP " + res.status);
                });
            }
            return res.json();
        })
        .then(function (data) {
            return data && data.status ? data.status : "Joining Call";
        });
}

function _cwCreateBotStatusState() {
    var currentStatus = null;
    return {
        shouldUpdate: function (next) { return currentStatus !== next; },
        set: function (next) { currentStatus = next; },
        clear: function () { currentStatus = null; },
    };
}

function _cwCreateBotStatusPoller(wrap, tickFn, intervalMs) {
    var intervalId = null;

    function tick() {
        if (!document.contains(wrap)) {
            stop();
            return;
        }
        tickFn();
    }

    function stop() {
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    function start() {
        tick();
        intervalId = setInterval(tick, intervalMs);
    }

    return { start: start, stop: stop };
}

function _cwBindBotStatusJoinHandler(joinBtn, conversationId, callbacks) {
    joinBtn.addEventListener("click", function () {
        joinBtn.disabled = true;
        if (callbacks.onStart) callbacks.onStart();
        postCalendarWidgetJoinNotetaker(conversationId)
            .then(function (status) {
                if (callbacks.isMounted && !callbacks.isMounted()) return;
                if (callbacks.onSuccess) callbacks.onSuccess(status);
            })
            .catch(function () {
                if (callbacks.isMounted && !callbacks.isMounted()) return;
                if (callbacks.onFailure) callbacks.onFailure();
            });
    });
}

function buildCalendarWidgetBotStatus(conv) {
    var els = _cwBuildBotStatusDom();
    var state = _cwCreateBotStatusState();

    function applyStatus(rawStatus) {
        if (!state.shouldUpdate(rawStatus)) return;
        state.set(rawStatus);
        _cwRenderBotStatus(els, rawStatus);
    }

    function applyJoinFailed() {
        if (!state.shouldUpdate("__JOIN_FAILED__")) return;
        state.set("__JOIN_FAILED__");
        _cwRenderBotStatusJoinFailed(els);
    }

    var poller = _cwCreateBotStatusPoller(els.wrap, function () {
        fetchCalendarWidgetBotStatus(conv.id)
            .then(function (status) {
                if (!document.contains(els.wrap)) return;
                applyStatus(status);
            })
            .catch(function () {
                if (!document.contains(els.wrap)) return;
                applyStatus("Unknown");
            });
    }, CW_BOT_STATUS_POLL_INTERVAL_MS);

    _cwBindBotStatusJoinHandler(els.joinBtn, conv.id, {
        isMounted: function () { return document.contains(els.wrap); },
        onStart: function () {
            state.clear();
            applyStatus("Joining Call");
        },
        onSuccess: function (status) {
            state.clear();
            applyStatus(status);
        },
        onFailure: applyJoinFailed,
    });

    poller.start();
    return els.wrap;
}

function renderCalendarWidgetConversationRow(conv, dialogTypes) {
    var ev = conv.calendar_event;
    var isConversationProcessing = _cwIsConversationProcessing(conv && conv.status);
    var article = _cwEl("article", "CalendarWidgetItem" + (ev ? "" : " CalendarWidgetItem--muted"));
    article.setAttribute("role", "listitem");
    article.setAttribute("tabindex", "0");

    var top = _cwEl("div", "CalendarWidgetItem__top");
    top.appendChild(_cwEl("p", "CalendarWidgetItem__title", ev && ev.title ? ev.title : "Conversation"));

    var meta = _cwEl("div", "CalendarWidgetItem__meta");
    var participants = _cwEl("span", "CalendarWidgetItem__participants");
    participants.setAttribute("title", "Participants");
    var groupIcon = _cwEl("i", "ri-group-line");
    groupIcon.setAttribute("aria-hidden", "true");
    participants.appendChild(groupIcon);
    participants.appendChild(_cwEl("span", null, String(conv.participants_count || 0)));
    meta.appendChild(participants);
    top.appendChild(meta);

    var calendarEventIdForDialogType = ev && ev.id != null ? String(ev.id) : null;

    var bottom = _cwEl("div", "CalendarWidgetItem__bottom");
    var timeWrap = _cwEl("div", "CalendarWidgetItem__time");
    timeWrap.appendChild(_cwEl("span", null,
        ev ? formatCalendarWidgetTimeRange(ev.start_time, ev.end_time)
           : formatCalendarWidgetTimeRange(conv.took_place, null)
    ));
    bottom.appendChild(timeWrap);

    var isMeetingNotStarted = false;
    if (ev && ev.start_time) {
        var startMs = new Date(ev.start_time).getTime();
        isMeetingNotStarted = !isNaN(startMs) && startMs > Date.now();
    }

    var rightWrap = _cwEl("div", "CalendarWidgetItem__right");
    var linkAnchor = null;
    if (conv.status === "generated") {
        linkAnchor = document.createElement("a");
        linkAnchor.className = "CalendarWidgetItem__link";
        linkAnchor.href = "/candidates/conversation/" + encodeURIComponent(conv.id);
        linkAnchor.setAttribute("title", "Open report");
        linkAnchor.setAttribute("aria-label", "Open report");
        var reportIcon = document.createElement("i");
        reportIcon.className = "ri-file-text-line";
        reportIcon.setAttribute("aria-hidden", "true");
        linkAnchor.appendChild(reportIcon);
        linkAnchor.appendChild(document.createTextNode("Report"));
    }
    else if (conv.status === "action required") {
        linkAnchor = document.createElement("a");
        linkAnchor.className = "CalendarWidgetItem__link";
        linkAnchor.href = "/conversation/actions/" + encodeURIComponent(conv.id);
        linkAnchor.setAttribute("title", "Actions");
        linkAnchor.setAttribute("aria-label", "Action required");
        var actionIcon = document.createElement("i");
        actionIcon.className = "ri-alert-line";
        actionIcon.setAttribute("aria-hidden", "true");
        linkAnchor.appendChild(actionIcon);
        linkAnchor.appendChild(document.createTextNode("Action required"));
    }
    else if (ev && ev.url && isMeetingNotStarted) {
        linkAnchor = document.createElement("a");
        linkAnchor.className = "CalendarWidgetItem__link";
        linkAnchor.href = ev.url;
        linkAnchor.target = "_blank";
        linkAnchor.rel = "noopener noreferrer";
        linkAnchor.setAttribute("title", "Join meeting");
        linkAnchor.setAttribute("aria-label", "Join meeting");
        var meetingIcon = document.createElement("i");
        meetingIcon.className = "ri-video-line";
        meetingIcon.setAttribute("aria-hidden", "true");
        linkAnchor.appendChild(meetingIcon);
        linkAnchor.appendChild(document.createTextNode("Meeting"));
    }
    if (linkAnchor) rightWrap.appendChild(linkAnchor);

    var toggleNotice = null;
    if (ev && ev.id) {
        var toggleWrap = _cwEl("div", "CalendarWidgetItem__toggle form-check form-switch m-0");
        var input = document.createElement("input");
        input.type = "checkbox";
        input.className = "form-check-input";
        input.setAttribute("role", "switch");
        input.checked = !ev.dodge;
        input.setAttribute("aria-label", "Attending (calendar bot)");
        toggleNotice = _cwEl("p", "CalendarWidgetItem__notice");
        toggleNotice.style.display = "none";

        var toggleBlocked = false;
        var blockToggle = function (reason) {
            toggleBlocked = true;
            input.disabled = true;
            input.title = reason;
        };
        var showToggleNotice = function (text) {
            toggleNotice.textContent = text;
            toggleNotice.style.display = "";
        };

        var blockReason = _cwDodgeBlockReason(isConversationProcessing, isMeetingNotStarted, ev);
        if (blockReason) blockToggle(blockReason);

        input.addEventListener("change", function () {
            if (toggleBlocked) return;
            var dodge = !input.checked;
            input.disabled = true;
            toggleNotice.style.display = "none";

            var p = ev.is_recurring
                ? showRecurringPrompt(article)
                : Promise.resolve(false);

            p.then(function (asRecurring) {
                return postCalendarEventDodge(ev.id, dodge, asRecurring)
                    .then(_cwReadDodgeResponse, function () { return { ok: false, status: 0, error: "" }; });
            }, function () {
                return null;
            }).then(function (result) {
                if (result && result.ok) {
                    ev.dodge = dodge;
                    return;
                }
                input.checked = !ev.dodge;
                if (!result) return;
                showToggleNotice(result.error || "Could not update attendance.");
                // 403 means the row is stale: the server state moved on since render.
                if (result.status === 403 && result.error) blockToggle(result.error);
            }).then(function () {
                input.disabled = toggleBlocked;
            });
        });
        toggleWrap.appendChild(input);
        rightWrap.appendChild(toggleWrap);
}

    if (rightWrap.childNodes.length) bottom.appendChild(rightWrap);

    var badges = _cwEl("div", "CalendarWidgetItem__badges");
    if (conv.status) {
        badges.appendChild(_cwEl("span", "CalendarWidgetItem__badge CalendarWidgetItem__badge--status", conv.status));
    }

    article.appendChild(top);
    if (badges.childNodes.length) article.appendChild(badges);
    article.appendChild(bottom);
    if (toggleNotice) article.appendChild(toggleNotice);
    if (Array.isArray(dialogTypes)) {
        article.appendChild(buildCalendarWidgetDialogTypeControl(conv, dialogTypes, calendarEventIdForDialogType));
    }

    if (_cwShouldShowBotStatus(conv)) {
        article.appendChild(buildCalendarWidgetBotStatus(conv));
    }

    article.addEventListener("click", function (evClick) {
        if (shouldIgnoreCalendarWidgetItemPopupClick(evClick.target)) return;
        if (_cwConversationPopupConversationId === conv.id) {
            closeCalendarWidgetConversationPopup();
            return;
        }
        openCalendarWidgetConversationPopup(article, conv);
    });

    article.addEventListener("keydown", function (evKey) {
        if (evKey.key !== "Enter" && evKey.key !== " ") return;
        if (shouldIgnoreCalendarWidgetItemPopupClick(evKey.target)) return;
        evKey.preventDefault();
        if (_cwConversationPopupConversationId === conv.id) {
            closeCalendarWidgetConversationPopup();
            return;
        }
        openCalendarWidgetConversationPopup(article, conv);
    });

    return article;
}

function loadCalendarWidgetConversations() {
    var listEl = document.getElementById("calendarWidgetList");
    if (!listEl) return;
    closeCalendarWidgetRecurringPopup(true);
    closeCalendarWidgetConversationPopup();

    var dateStr = getCalendarWidgetChosenDateYmd();
    document.getElementById("calendarWidgetTitle").textContent =
        new Date(dateStr).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

    listEl.innerHTML = "";
    listEl.appendChild(_cwEl("p", "body-text-sm-regular in:text-gray-500 in:m-0", "Loading\u2026"));

    var conversationsPromise = fetch("/calendar/widget/conversations/?date=" + encodeURIComponent(dateStr), { credentials: "same-origin" })
        .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
        });

    Promise.all([conversationsPromise, fetchCalendarWidgetDialogTypes().catch(function () { return null; })])
        .then(function (results) {
            var data = results[0];
            var dialogTypes = results[1];
            listEl.innerHTML = "";
            var rows = Array.isArray(data) ? data : [];
            if (!rows.length) {
                listEl.appendChild(_cwEl("p", "body-text-sm-regular in:text-gray-500 in:m-0", "No conversations for this day."));
                return;
            }
            for (var i = 0; i < rows.length; i++) {
                listEl.appendChild(renderCalendarWidgetConversationRow(rows[i], dialogTypes));
            }
        })
        .catch(function () {
            listEl.innerHTML = "";
            listEl.appendChild(_cwEl("p", "body-text-sm-regular in:text-red-500 in:m-0", "Could not load calendar conversations."));
        });
}

function initCalendarWidgetConversations() {
    if (!document.getElementById("calendarWidgetList")) return;
    initCalendarWidgetDocumentUpload();
    loadCalendarWidgetConversations();
    window.addEventListener("storage", function (e) {
        if (e.key === "calendarWidgetChosenDate") loadCalendarWidgetConversations();
    });
    window.refreshCalendarWidgetConversations = loadCalendarWidgetConversations;
}

// Each part returns early when its markup isn't on the page.
const init = () => {
  initCalendarWidgetSidebar();
  initCalendarWidgetMonthGrid();
  initCalendarWidgetConversations();
};

document.addEventListener("DOMContentLoaded", init);
